import { useEffect, useState } from "react";
import {
  formatKebabPrice,
  type KebabEntry,
} from "@ziolo/shared";
import { Button } from "@ziolo/ui";
import { formatTimestamp } from "../../lib/format.js";

interface LightboxProps {
  entries: KebabEntry[];
  currentId: number | null;
  onClose: () => void;
  onStep: (delta: number) => void;
  onDelete?: ((id: number) => void | Promise<void>) | undefined;
}

export function KebabLightbox({ entries, currentId, onClose, onStep, onDelete }: LightboxProps) {
  const index = entries.findIndex((entry) => entry.id === currentId);
  const entry = index >= 0 ? entries[index] : null;
  const [touchStart, setTouchStart] = useState<number | null>(null);

  useEffect(() => {
    if (currentId === null) return;

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onStep(-1);
      if (event.key === "ArrowRight") onStep(1);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentId, onClose, onStep]);

  if (!entry) return null;

  return (
    <div
      className="kb-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Zdjęcie kebaba"
      onClick={onClose}
    >
      <button type="button" className="kb-lightbox__close" onClick={onClose} aria-label="Zamknij">
        ×
      </button>
      {entries.length > 1 ? (
        <>
          <button
            type="button"
            className="kb-lightbox__nav kb-lightbox__nav--prev"
            onClick={(event) => {
              event.stopPropagation();
              onStep(-1);
            }}
            aria-label="Poprzednie"
          >
            ‹
          </button>
          <button
            type="button"
            className="kb-lightbox__nav kb-lightbox__nav--next"
            onClick={(event) => {
              event.stopPropagation();
              onStep(1);
            }}
            aria-label="Następne"
          >
            ›
          </button>
        </>
      ) : null}
      <div
        className="kb-lightbox__body"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => setTouchStart(event.changedTouches[0]?.screenX ?? null)}
        onTouchEnd={(event) => {
          if (touchStart === null) return;
          const delta = event.changedTouches[0]?.screenX ?? touchStart;
          if (touchStart - delta > 50) onStep(1);
          if (delta - touchStart > 50) onStep(-1);
          setTouchStart(null);
        }}
      >
        {entry.imageUrl ? (
          <img src={entry.imageUrl} alt={`Kebab ${entry.contestantName}`} />
        ) : null}
        <div className="kb-lightbox__meta">
          <strong>{entry.contestantName}</strong>
          <span>{formatTimestamp(entry.createdAt)}</span>
          {entry.location ? <span>📍 {entry.location}</span> : null}
          {entry.price !== null ? <span>💰 {formatKebabPrice(entry.price)}</span> : null}
          {entry.notes ? <span>💬 {entry.notes}</span> : null}
          {onDelete ? (
            <Button
              size="sm"
              variant="danger"
              onClick={(event) => {
                event.stopPropagation();
                void onDelete(entry.id);
              }}
            >
              Usuń
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
